import { PaymentModuleId } from '@foundation/apis/payments';
import Clarity from '@microsoft/clarity';
import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import cn from 'clsx';
import { JSX } from 'react';

import { disableUIBlocker, enableUIBlocker } from '@/components/common';
import { RedirectStatus } from '@/components/contexts/params/schemas/RedirectStatus';
import { PayButton } from '@/components/pages/stored-instruments/utils/PayButton';
import { useUserFriendlyPaymentModuleName } from '@/hooks/useUserFriendlyPaymentModuleName';
import { triggerErrorToast, triggerInfoToast } from '@/libs/toasts';
import Logger, { LoggerOrigin } from '@/services/Logger';
import NavigationService, {
  InternalPage,
} from '@/services/navigation/NavigationService';
import { NUVEI_TWINT_CANCEL_ERROR_CODE } from '@/services/nuvei/NuveiCardService';
import { NuveiTransactionResponse } from '@/services/nuvei/NuveiTransactionResponse';
import NuveiTwintService from '@/services/nuvei/NuveiTwintService';
import PaymentStore from '@/services/payments/PaymentStore';
import TelemetryService from '@/services/telemetry/TelemetryService';

interface IPayWithNuveiTwintBtnProps {
  navigation?: JSX.Element | null;
}

export function PayWithNuveiTwintBtn({
  navigation,
}: IPayWithNuveiTwintBtnProps) {
  const { accessToken } = useAuth();
  const { t } = useTranslation();
  const paymentModuleT = useUserFriendlyPaymentModuleName();

  return (
    <div className="relative">
      {
        <div className={cn('flex flex-col mt-md', 'visible')}>
          <div className={cn('flex mt-md space-x-2')}>
            {navigation}
            <PayButton
              onClick={async () => {
                try {
                  enableUIBlocker();

                  const result = await NuveiTwintService.createNewPayment(
                    accessToken ?? '',
                  );

                  if (result.result === 'APPROVED') {
                    if (result.errCode === '0') {
                      // "Ok" button was clicked in the TWINT app
                      Clarity.setTag('pay', [
                        RedirectStatus.Success,
                        paymentModuleT(PaymentModuleId.NuveiTwint),
                      ]);
                      NavigationService.navigateTo({
                        page: InternalPage.StatusSuccess,
                      });
                    } else {
                      triggerErrorToast(
                        t('Something went wrong!'),
                        t(
                          'Operation failed. Please try again with a different payment method.',
                        ),
                      );
                      Clarity.setTag('pay', [
                        RedirectStatus.Failure,
                        paymentModuleT(PaymentModuleId.NuveiTwint),
                      ]);
                    }
                  } else if (result.result === 'DECLINED') {
                    // "Cancel" button was clicked in the TWINT app

                    //errCode = 0 - when TWINT mobile app shows just 'Cancel' buttons and user clicked 'Cancel'
                    //errCode = 1218 - when TWINT mobile app shows both 'Ok' and 'Cancel' buttons and user clicked 'Cancel'
                    //error code 1218 is not listed here https://docs.nuvei.com/documentation/integration/response-handling/ , so it's hard to say what exactly this means
                    triggerInfoToast(
                      t('Select a new payment method and proceed to pay.'),
                    );
                    Clarity.setTag('pay', [
                      RedirectStatus.Canceled,
                      paymentModuleT(PaymentModuleId.NuveiTwint),
                    ]);
                  }
                } catch (e) {
                  const error = e as Error;
                  TelemetryService.trackException(
                    error,
                    'PayWithNuveiTwintBtn',
                    {
                      pid: PaymentStore.paymentIntentId,
                    },
                  );
                  Logger.error(
                    LoggerOrigin.ReactComponent,
                    'PayWithNuveiTwintBtn failed to pay with Nuvei TWINT.',
                    error,
                  );

                  const response = e as NuveiTransactionResponse;
                  if (response.result === 'ERROR') {
                    if (response.errCode === NUVEI_TWINT_CANCEL_ERROR_CODE) {
                      //'Return' or Close popup
                      triggerInfoToast(
                        t('Select a new payment method and proceed to pay.'),
                      );
                      Clarity.setTag('pay', [
                        RedirectStatus.Canceled,
                        paymentModuleT(PaymentModuleId.NuveiTwint),
                      ]);
                    } else {
                      triggerErrorToast(
                        t('Something went wrong!'),
                        t(
                          'Operation failed. Please try again with a different payment method.',
                        ),
                      );
                      Clarity.setTag('pay', [
                        RedirectStatus.Failure,
                        paymentModuleT(PaymentModuleId.NuveiTwint),
                      ]);
                    }
                  }
                } finally {
                  disableUIBlocker();
                }
              }}
              title={t('Proceed to pay')}
            />
          </div>
        </div>
      }
    </div>
  );
}
